import { ONE } from '../lib/gwei';
import { useProductPricesSub } from './useProductPricesSub';
import { useProducts } from './useProducts';

export type TokenPrices = Record<string, bigint>;

const USD_SYMBOLS = ['USD', 'USDe'];

/**
 * Placeholder hook to get token prices in USD. Currently there's no endpoint,
 * so we return $1 for USD/USDe, and for other tokens we try to find the
 * associated product's price. E.g., for BTC, we would return the price of
 * BTCUSD.
 */
export const useTokenPrices = (
  tokenSymbols: string[]
): { data: TokenPrices; isLoading: boolean; error: Error | undefined } => {
  const { data: products, isLoading: loadingProducts, error: productsError } = useProducts();

  // Create map of token symbol to associated product against USD.
  const tokenProductMap = new Map(
    products
      ?.filter(
        (product) =>
          tokenSymbols.includes(product.baseTokenName) &&
          USD_SYMBOLS.includes(product.quoteTokenName)
      )
      .map((product) => [product.baseTokenName, product])
  );

  const { data: priceByProductId } = useProductPricesSub();

  // Create final price map by checking each token
  const priceMap = Object.fromEntries(
    tokenSymbols
      .map((symbol) => {
        if (USD_SYMBOLS.includes(symbol)) {
          return [symbol, ONE];
        }

        const product = tokenProductMap.get(symbol);
        return [symbol, product ? priceByProductId[product.id] : undefined];
      })
      .filter(([, price]) => price !== undefined)
  ) as TokenPrices;

  return {
    data: priceMap,
    isLoading: loadingProducts,
    error: productsError ?? undefined,
  };
};
